"use client";

/**
 * Inline-editable text node.
 *
 * Default mode: renders the resolved string as plain text inside an
 * element of the caller's choosing (default `<span>`).
 *
 * Edit mode (admin + editMode on): outlines the element, makes it
 * `contentEditable`, and pushes every blur into the EditContext as a
 * pending change. The element gets a small "✎ <id>" hover badge so the
 * editor can see which content key they're touching.
 *
 * For multi-line copy (paragraphs) pass `multiline`. Linebreaks become
 * `\n` on save and are rendered with `whitespace-pre-line`.
 *
 * IMPORTANT: this component is intentionally minimal so it can be dropped
 * inside complex animated wrappers (motion.div, KineticText, etc.) without
 * disturbing the layout. It never adds extra padding/margin in default mode.
 */

import { useEffect, useRef, type ElementType, type CSSProperties } from "react";
import { useEditContext } from "@/lib/edit-context";
import MoveHandle, { useMoveOffset } from "@/components/MoveHandle";
import EditorIsland from "@/components/EditorIsland";

type Props = {
  /** Stable content-block id, e.g. "hero.title". */
  id: string;
  /** Default value used when nothing is in DB and nothing is queued. */
  defaultValue: string;
  /** Element tag to render. Default `<span>`. */
  as?: ElementType;
  className?: string;
  /** Optional inline style forwarded onto the rendered element. Useful
   *  for textShadow / paddingX / etc that callers like KineticText apply
   *  on the underlying tag. */
  style?: CSSProperties;
  /** Allow line breaks (Enter inserts newline instead of committing). */
  multiline?: boolean;
  /** Optional placeholder shown if value is empty in edit mode. */
  placeholder?: string;
  /** Optional test id to forward to the rendered element. */
  "data-testid"?: string;
};

function EditableTextInner({
  id,
  defaultValue,
  as: Tag = "span",
  className = "",
  style,
  multiline = false,
  placeholder = "Click to edit…",
  "data-testid": testId,
}: Props) {
  const { isAdmin, editMode, getValue, setValue } = useEditContext();
  const raw = getValue(id, defaultValue);
    // getValue already returns `defaultValue` when the id was never set,
    // and the explicit stored value when it was — which MAY be an empty
    // string if an admin deliberately cleared the field. We honour `raw`
    // verbatim: an empty value means "intentionally blank" and the element
    // collapses below (return null) so the surrounding layout reflows and
    // the leftover gap closes up, instead of snapping back to the default.
    const value: string = typeof raw === "string" ? raw : defaultValue;
  const editing = isAdmin && editMode;
  const ref = useRef<HTMLElement | null>(null);

  // Keep the DOM in sync when value changes externally (undo / discard /
  // remote save). We only write the DOM when the user is NOT currently
  // focused inside the element, otherwise we'd kill their caret.
  // For multiline text we render newline characters as <br> so the
  // visual layout matches what the user typed (otherwise textContent
  // would put everything on one line).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (multiline) {
      // Convert "\n" → <br>. Escape HTML to keep it safe.
      // Defensive: never let a non-string value reach the DOM
        // or we'd wipe whatever React rendered.
        const safeValue: string = typeof value === "string" ? value : "";
        const escaped = safeValue
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br>");
        if (el.innerHTML !== escaped) el.innerHTML = escaped;
      } else {
        const safeValue: string = typeof value === "string" ? value : "";
        if (el.textContent !== safeValue) el.textContent = safeValue;
      }
  }, [value, multiline]);

  // Suppress hydration warning on first paint: server renders the saved
  // value, client may briefly show the same value but with contentEditable
  // attributes, which React would otherwise complain about.
  /* v6.13.49 — Added explicit caret colour. The contentEditable spans
     inherit `caret-color: auto`, which on most browsers maps to the
     element's `color` value — and many of our editable surfaces sit
     on near-black glass cards with white text, so the white caret
     reads correctly against dark backgrounds BUT the user reported
     "cursor still not seen on edit box cards" — they're editing
     against bright amber/violet/cyan tinted card surfaces where the
     white caret blends into the tint. Forcing `caret-amber-300`
     gives a high-contrast warm caret on every surface (dark glass,
     coloured tints, white hero panels alike) so the typing position
     is always unmistakable. */
  const baseClass = editing
    ? `relative cursor-text caret-amber-300 outline-none rounded-sm ring-1 ring-amber-300/0 hover:ring-amber-300/60 focus:ring-amber-300/90 transition-shadow min-w-[2ch] empty:before:content-[attr(data-placeholder)] empty:before:opacity-30 empty:before:pointer-events-none ${className}`
    : className;
  // Multiline text MUST keep its `\n` characters and consecutive spaces
  // visible in BOTH edit and view modes — otherwise an admin who adds a
  // line break or extra spaces will see the text revert after saving.
  // `whitespace-pre-line` preserves newlines but still collapses
  // consecutive spaces (e.g. " " → single space), so spaces appear to
  // revert on save. `whitespace-pre-wrap` preserves BOTH newlines AND
  // whitespace sequences while still soft-wrapping at word boundaries.
  const wrapClass = multiline ? `${baseClass} whitespace-pre-wrap` : baseClass;

  const onBlur = (e: React.FocusEvent<HTMLElement>) => {
    let next: string;
    if (multiline) {
      // Read the editable HTML and turn <br> / <div> into newlines so
      // that text the user wrapped onto multiple lines actually persists
      // as multi-line content (textContent alone strips them).
      // iOS Safari & Chrome wrap each new line in a <div> (sometimes
      // <div><br></div> for a blank line) instead of a bare <br>. The old
      // logic only turned CLOSING </div>/</p> and <br> into "\n", so an
      // OPENING <div> with no matching newline silently merged adjacent
      // lines — Enter (and the spaces typed after it) appeared to "not
      // register". Convert BOTH opening and closing block tags to newlines.
      const html = (e.currentTarget.innerHTML ?? "")
        .replace(/<div><br\s*\/?><\/div>/gi, "\n")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(div|p|h[1-6])>/gi, "")
        .replace(/<(div|p|h[1-6])[^>]*>/gi, "\n")
        .replace(/<[^>]+>/g, "");
      // Decode the basic HTML entities we previously escaped.
      const tmp = document.createElement("textarea");
      tmp.innerHTML = html;
      next = tmp.value.replace(/\u00A0/g, " ");
      // Collapse blank-line runs and strip leading/trailing newlines the
      // block-tag conversion can introduce (the first line is often wrapped
      // in its own <div> too, which would add a stray blank first line).
      next = next.replace(/\n{3,}/g, "\n\n").replace(/^\n+/, "").replace(/\n+$/, "");
    } else {
      next = (e.currentTarget.textContent ?? "").replace(/\u00A0/g, " ");
    }
    if (next !== value) setValue(id, next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    // Enter without shift commits in single-line mode.
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      (e.currentTarget as HTMLElement).blur();
    }
    // Esc cancels: restore textContent to the saved value, then blur.
    if (e.key === "Escape") {
      e.preventDefault();
      if (ref.current) ref.current.textContent = value;
      (e.currentTarget as HTMLElement).blur();
    }
  };

  // Stop accidental link navigation during edit.
  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!editing) return;
    const link = (e.target as HTMLElement).closest("a");
    if (link) e.preventDefault();
  };

  /* v6.13.42 — Per-element drag-to-reposition.
     The persisted (dx, dy) from useMoveOffset becomes a translate3d
     transform on the editable element itself, and a small "✥ MOVE"
     handle is rendered absolutely-positioned in the top-right corner
     in admin edit mode. The handle floats outside the editable text
     box (negative offset) so it never gets in the way of typing.
     Outside edit mode neither the handle nor any wrapper is added —
     the rendered DOM is byte-identical to the previous behaviour. */
  const { transform: moveTransform } = useMoveOffset(id);
    /* v6.13.64 — caret visibility fix. The Tailwind class
       `caret-amber-300` IS generated in production CSS (verified
       `.caret-amber-300{caret-color:#fcd34d}` ships in
       dea4105a66762aeb.css), but admins still report the typing
       caret is invisible on the EVADE box-cards. Setting caret color
       INLINE bypasses any cascade override and any mobile-safari
       quirks, and pinning WebkitTextFillColor to currentColor stops
       a transparent-text-fill inherited from a decorative parent
       from making the caret invisible. */
    const composedStyle: CSSProperties | undefined =
      moveTransform || style || editing
        ? {
            ...style,
            ...(moveTransform
              ? {
                  transform: style?.transform
                    ? `${moveTransform} ${style.transform}`
                    : moveTransform,
                }
              : {}),
            ...(editing
              ? {
                  caretColor: "#fbbf24",
                  WebkitTextFillColor: "currentColor",
                }
              : {}),
          }
        : undefined;

  // Visitors (and admins NOT in edit mode) should never see an empty
  // outlined box where cleared content used to be — render nothing so the
  // surrounding layout reflows and the blank gap closes up. Admins IN edit
  // mode keep the (now clickable, placeholder-bearing) element so they can
  // type new content back in.
  if (!editing && value.trim() === "") return null;

  const Component = Tag as ElementType;
  const editable = (
    <Component
      ref={ref as React.RefObject<HTMLElement>}
      className={wrapClass}
      style={composedStyle}
      contentEditable={editing}
      suppressContentEditableWarning
      // EditableText is frequently nested inside framer-motion wrappers
      // (ParallaxChapter, Reveal, KineticText) whose own `initial` /
      // motion-value-driven styles produce SSR/CSR `style` deltas that
      // React's reconciler blames on the nearest leaf — i.e. us. The
      // surrounding animations are intentional and benign, so we
      // suppress the warning at the leaf to keep the console clean
      // for visitors. We do NOT add this on the editable root itself
      // because contentEditable already handles its own warning.
      suppressHydrationWarning
      spellCheck={editing}
      data-editable-id={id}
      data-move-target={id}
      data-testid={testId}
      data-placeholder={placeholder}
      onBlur={editing ? onBlur : undefined}
      onKeyDown={editing ? onKeyDown : undefined}
      onClick={onClick}
    >
      {value}
    </Component>
  );

  if (!editing) return editable;

  // In edit mode wrap in a position:relative span so the MoveHandle
  // can anchor itself. The wrapper is `inline-block` (single-line) or
  // `block` (multiline) so it doesn't disturb the surrounding flow.
  return (
    <span
      className={`relative ${multiline ? "block" : "inline-block"} align-baseline`}
    >
      {editable}
      <MoveHandle id={id} />
    </span>
  );
}

export default function EditableText(props: Props) {
  return (
    <EditorIsland id={props.id}>
      <EditableTextInner {...props} />
    </EditorIsland>
  );
}
